import { Link } from "react-router-dom";
import { ArrowRight, MapPin, BedDouble } from "lucide-react";
import { Reveal, Stagger, Item, SectionHead } from "./shared";
import { useI18n } from "../i18n/LanguageContext";
import { useProjects } from "../hooks/useProjects";

const Card = ({ p, fromLabel }) => (
  <Item>
    <Link to={`/projects/${p.id}`} data-testid={`project-card-${p.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-3xl bg-white shadow-soft transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-card">
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110" />
        <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-navy backdrop-blur-sm">{p.tag}</span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-center gap-1.5 text-muted"><MapPin size={14} className="text-gold" /><span className="text-sm">{p.location}</span></div>
        <h3 className="mt-2 font-display text-2xl font-semibold leading-tight tracking-tight text-navy">{p.name}</h3>
        <p className="mt-1 text-sm text-muted">{p.developer}</p>
        <div className="mt-3 flex items-center gap-1.5 text-sm text-muted"><BedDouble size={15} className="text-gold" /> {p.bedrooms}</div>
        <div className="mt-auto flex items-end justify-between pt-6">
          <div>
            <span className="block text-xs uppercase tracking-wide text-muted">{fromLabel}</span>
            <span className="font-display text-xl font-semibold text-navy">{p.price} <span className="text-sm font-medium text-muted">{p.currency}</span></span>
          </div>
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-navy text-white transition-colors duration-300 group-hover:bg-gold">
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </Link>
  </Item>
);

export const Projects = () => {
  const { t, c } = useI18n();
  const { projects: items, loading } = useProjects(c.PROJECTS);
  return (
    <section id="projects" className="py-20 sm:py-28" data-testid="projects-section">
      <div className="container-x">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <SectionHead eyebrow={t.projects.eyebrow} title={t.projects.title} sub={t.projects.sub} />
          <Reveal delay={0.1}>
            <Link to="/projects" data-testid="projects-view-all" className="btn-ghost">{t.projects.viewAll} <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
        {loading ? (
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="projects-section-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="overflow-hidden rounded-3xl bg-white shadow-soft">
                <div className="aspect-[4/3] animate-pulse bg-navy/5" />
                <div className="space-y-3 p-6">
                  <div className="h-4 w-1/3 animate-pulse rounded bg-navy/5" />
                  <div className="h-6 w-2/3 animate-pulse rounded bg-navy/5" />
                  <div className="h-4 w-1/2 animate-pulse rounded bg-navy/5" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Stagger mountAnimate key={items.length ? items[0].id : "empty"} className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.slice(0, 6).map((p) => <Card key={p.id} p={p} fromLabel={t.projects.from} />)}
          </Stagger>
        )}
      </div>
    </section>
  );
};
